"use client";

import encryptLocalStorage from "localstorage-slim";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useReducer } from "react";
import { paths } from "routes/paths";
import { PASSING_DATE_FORMAT, STORAGE_KEY_PASSCODE, STORAGE_KEY_USER } from "utils/constant";
import { setSession } from "utils/utils";
import { AuthContext } from "./auth-context";

import { useRouter } from "next/navigation";

const initialState = {
    user: null,
    loading: true,
};

const reducer = (state, action) => {
    if (action.type === "INITIAL") {
        return {
            loading: false,
            user: action.payload.user,
        };
    }
    if (action.type === "LOGIN") {
        return {
            ...state,
            user: action.payload.user,
        };
    }
    if (action.type === "REGISTER") {
        return {
            ...state,
            user: action.payload.user,
        };
    }
    if (action.type === "LOGOUT") {
        return {
            ...state,
            user: null,
        };
    }
    return state;
};


export function AuthProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const router = useRouter();

    const initialize = useCallback(async () => {
        try {
            const user = encryptLocalStorage.get(STORAGE_KEY_USER, {
                decrypt: true,
                secret: STORAGE_KEY_PASSCODE,
            });
            if (user) {
                setSession(user);
                user?.currentWorkshop?.timezone && moment.tz.setDefault(user?.currentWorkshop?.timezone);
                dispatch({ type: "INITIAL", payload: { user: user } });
            } else {
                dispatch({ type: "INITIAL", payload: { user: null } });
            }
        } catch (error) {
            dispatch({ type: "INITIAL", payload: { user: null } });
        }
    }, []);

    useEffect(() => {
        initialize();
    }, [initialize]);

    const login = useCallback(async (user) => {
        const userInfo = { ...user, currentWorkshop: { ...user?.company, company_id: user?.company_id } };
        setSession(userInfo);
        encryptLocalStorage.set(STORAGE_KEY_USER, userInfo, { encrypt: true, secret: STORAGE_KEY_PASSCODE });
        dispatch({ type: "LOGIN", payload: { user: userInfo } });
    }, []);


    // LOGOUT
    const logout = useCallback(async () => {
        if (typeof window !== "undefined") {
            encryptLocalStorage.clear()
            setSession(null);
        }
        dispatch({ type: "LOGOUT" });
        router.replace(paths.root);
    }, []);

    const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

    const status = state.loading ? "loading" : checkAuthenticated;

    const memoizedValue = useMemo(() => {
        const user = state.user;
        const currentWorkshop = user?.currentWorkshop;
        const country = currentWorkshop?.country;

        return {
            initialize,
            login,
            logout,
            user,
            method: "jwt",
            loading: status === "loading",
            authenticated: status === "authenticated",

            isSuperAdmin: currentWorkshop?.access_by === 'sa', // Check if user has super admin access
            isHasWebsite: currentWorkshop?.has_website == 'yes', // Whether the account has a website
            moduleMarketPlace: currentWorkshop?.module_marketplace == 'yes', // Marketplace module enabled
            moduleInventoryTire: currentWorkshop?.module_inventory_tire == 'yes', // Inventory tires module enabled
            moduleMembershipPlan: currentWorkshop?.module_amc == 'yes', // Module Membership package module enabled

            // Business information
            companyInfoSet: currentWorkshop?.company_info === 'yes', // Company info set status
            businessRegistered: currentWorkshop?.business_registered === 'yes' || false, // Business registration status
            businessRegisterType: currentWorkshop?.business_registered_type ?? 'regular', // Type of business registration

            // Formatting and preferences
            DATE_FORMAT: currentWorkshop?.web_date_format ?? PASSING_DATE_FORMAT, // Date format
            connectWithZoho: currentWorkshop?.book_keeping_preference === 'zoho', // Connection with Zoho for bookkeeping

            // Country-specific settings
            DECIMAL_VALUE: country?.decimal_value ?? 2, // Decimal precision
            // CURRENCY_SYMBOL: country?.currency_symbol ?? "₹", // Currency symbol
            CURRENCY_SYMBOL: country?.currency_symbol ?? "", // Currency symbol
            CURRENCY_NAME: country?.currency ?? "INR", // Currency name
            SHOW_HSN_CODE: country?.phonecode === "91", // Show HSN code for India
            isGcc: country?.region_code === "GCC", // Check if region is GCC (AE, SA, BH, KW, OM, QA),

        };
    }, [initialize, login, logout, state.user, status]);

    return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
    children: PropTypes.node,
};
